import CommonModal from "@components/common/CommonModal";
import { useUser } from "@/context/useUser";
import { UserPreferencesService } from "@/services/APIs";
import { gridName } from "@/utils/constant/gridName";
import { DynamicTableProps } from "@/utils/types/baseTable";
import { ColumnState } from "@/utils/types/userPreferencesGrid";
import { ProTable } from "@ant-design/pro-components";
import { Table, TableProps } from "antd";
import { useEffect, useMemo, useState } from "react";

type AntTableSize = NonNullable<TableProps<object>["size"]>;

function normalizeSize(v: unknown): AntTableSize {
  if (v === "default" || v === "large") {
    return "large";
  }
  if (v === "compact" || v === "small") {
    return "small";
  }
  return "middle";
}

// This component is specifically for ModulePage with dynamic functionality
const DynamicTable = <T extends object>({
  loading,
  columns,
  dataSource,
  page,
  pageSize,
  total,
  onPageChange,
  headerTitle,
  rowKey,
  toolBarRender,
  scroll = {},
  search = false,
  showModal = false,
  modalTitle = "",
  modalContent,
  onModalClose,
  selectedRowKeys,
  setSelectedRowKeys,
  request,
  tableName,
}: DynamicTableProps<T> & {
  tableName: string;
}) => {
  const paginationConfig = {
    current: page,
    pageSize,
    total,
    onChange: onPageChange,
    showTotal: (total: number, range: [number, number]) =>
      `${range[0]}-${range[1]} of ${total}`,
    showSizeChanger: true,
    showQuickJumper: true,
  };

  const { preferencesData, fetchPreferencesData } = useUser();
  const [columnsState, setColumnsState] = useState<ColumnState>({});
  const [size, setSize] = useState<AntTableSize>("middle");
  const defaultColumnsState = useMemo(() => {
    const state: ColumnState = {};
    columns.forEach(col => {
      if (col.dataIndex) {
        state[col.dataIndex as string] = { show: true }; // default show true
      }
    });
    return state;
  }, [columns]);
  useEffect(() => {
    if (preferencesData && preferencesData.length > 0) {
      const colPref = preferencesData.find(item => item.key === tableName);
      setColumnsState((colPref?.json as ColumnState) ?? {});

      const saved = preferencesData.find(
        item => item.key === gridName.settingsTable
      )?.json;

      setSize(normalizeSize(saved));
    }
  }, [preferencesData, tableName]);

  const handleColumnsStateChange = async (newState: ColumnState) => {
    setColumnsState(newState);
    if (tableName) {
      const payload = {
        key: tableName,
        json:
          Object.keys(newState).length === 0 ? defaultColumnsState : newState,
      };
      await UserPreferencesService.addUserPreferencesGrid(payload);
      fetchPreferencesData();
    }
  };

  async function handleChangeSize(v: unknown) {
    const normalized = normalizeSize(v);
    setSize(normalized);
    // persist the normalized value
    const payload = {
      key: gridName.settingsTable,
      json: normalized,
    };
    await UserPreferencesService.addUserPreferencesGrid(payload);
    fetchPreferencesData();
  }

  return (
    <>
      <ProTable<T>
        rowKey={rowKey}
        rowSelection={{
          selectedRowKeys,
          columnWidth: 60,
          onChange: newSelectedRowKeys =>
            setSelectedRowKeys?.(newSelectedRowKeys),
          selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT],
        }}
        loading={loading}
        columns={columns}
        dataSource={dataSource}
        search={search ? {} : false}
        pagination={paginationConfig}
        toolBarRender={toolBarRender}
        columnsState={{
          value: columnsState,
          onChange: handleColumnsStateChange,
        }}
        size={size}
        onSizeChange={handleChangeSize}
        scroll={scroll}
        headerTitle={headerTitle}
        request={request}
      />

      {showModal && (
        <CommonModal
          open={showModal}
          title={modalTitle}
          footer={null}
          onCancel={onModalClose}
          destroyOnClose
        >
          {modalContent}
        </CommonModal>
      )}
    </>
  );
};

export default DynamicTable;
